import styled, { css } from 'styled-components';

import { COLOR, textStyles } from '#@/shared/utils/styles';

interface LabelProps {
  $alignItems: 'flex-start' | 'center' | 'flex-end' | undefined;
  $disabled: boolean | undefined;
  $hasDescription: boolean;
  $hasLabel: boolean;
}

export const Label = styled.label<LabelProps>`
  align-items: ${({ $alignItems = 'flex-start' }) => $alignItems};
  display: grid;
  gap: 4px 12px;
  grid-template-areas: ${({ $hasDescription, $hasLabel }) => {
    if ($hasLabel && $hasDescription) {
      return "'radioButton labelText' '. description'";
    }
    if ($hasLabel) {
      return "'radioButton labelText'";
    }
    return "'radioButton'";
  }};
  grid-template-columns: max-content;
  padding: 2px;
  ${({ $hasDescription }) =>
    $hasDescription ? textStyles.labelMedium500 : textStyles.bodyMedium450};
  color: ${COLOR.textSecondary};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
`;

export const Description = styled.div`
  ${textStyles.bodyMedium450};

  color: ${COLOR.textTertiary};
  grid-area: description;
`;

const hoverStyles = css`
  /* When :focus-visible, Chrome seems to apply extra offset for some reason */
  outline-offset: 0;

  &:not(:checked):not(:disabled) {
    border: 1px solid ${COLOR.highlightNeon};
  }
`;

interface StyledRadioButtonProps {
  $hoverGroupName: string | undefined;
}

export const StyledRadioButton = styled.input.attrs({ type: 'radio' })<StyledRadioButtonProps>`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin: 0;
  border: 1px solid ${COLOR.borderMedium};
  border-radius: 100%;
  background: ${COLOR.white};
  cursor: pointer;
  grid-area: radioButton;

  &:checked {
    border: 5px solid ${COLOR.brand};
  }

  ${(props) =>
    props.$hoverGroupName &&
    css`
      html:has([data-hover-group='${props.$hoverGroupName}']:hover)
        &:is([data-hover-group='${props.$hoverGroupName}']) {
        ${hoverStyles}
      }
    `}

  &:hover {
    ${hoverStyles}
  }

  &:focus:not(:checked):not(:disabled),
  &:focus-visible:not(:disabled) {
    outline: 2px solid ${COLOR.brandLightAlpha};
  }

  &:disabled {
    border-color: ${COLOR.backgroundMedium};
    background: ${COLOR.backgroundMedium};
    cursor: not-allowed;

    &:checked {
      background: ${COLOR.textTertiary};
    }
  }
`;

export const LabelText = styled.div<{
  $alignItems: 'flex-start' | 'center' | 'flex-end' | undefined;
}>`
  align-items: ${({ $alignItems }) => $alignItems};
  display: flex;
  gap: 8px;
  grid-area: labelText;
`;
